// Cognito User Pools の HTTPS JSON API を fetch で直接呼ぶ最小クライアント（実行時 SDK 依存ゼロ）。
// SignInPolicy.AllowedFirstAuthFactors は PASSWORD/PASSWORD_SRP に固定（設計 §2.2/R15）。
// 本 MVP は USER_PASSWORD_AUTH を使用（HTTPS 経由のみ。SRP の数学的フローはサーバ側 SignInPolicy で許容範囲を制限済み）。

export interface InitiateAuthInput {
  readonly clientId: string;
  readonly authFlow: "USER_PASSWORD_AUTH" | "USER_SRP_AUTH";
  readonly authParameters: Record<string, string>;
}

export interface RespondToAuthChallengeInput {
  readonly clientId: string;
  readonly challengeName: string;
  readonly session: string;
  readonly challengeResponses: Record<string, string>;
}

export interface AuthResult {
  readonly AuthenticationResult?: {
    readonly AccessToken?: string;
    readonly IdToken?: string;
    readonly RefreshToken?: string;
  };
  readonly ChallengeName?: string;
  readonly Session?: string;
}

export interface CognitoIdp {
  initiateAuth(input: InitiateAuthInput): Promise<AuthResult>;
  respondToAuthChallenge(input: RespondToAuthChallengeInput): Promise<AuthResult>;
}

function endpoint(region: string): string {
  return `https://cognito-idp.${region}.amazonaws.com/`;
}

async function call<T>(region: string, target: string, body: unknown): Promise<T> {
  const res = await fetch(endpoint(region), {
    method: "POST",
    headers: {
      "content-type": "application/x-amz-json-1.1",
      "x-amz-target": `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as Record<string, unknown>;
  if (!res.ok) {
    const type = typeof json["__type"] === "string" ? (json["__type"] as string) : "CognitoError";
    const message = typeof json["message"] === "string" ? (json["message"] as string) : type;
    throw new Error(`${type}: ${message}`);
  }
  return json as T;
}

export function createCognitoIdp(region: string): CognitoIdp {
  return {
    initiateAuth: (input) =>
      call<AuthResult>(region, "InitiateAuth", {
        ClientId: input.clientId,
        AuthFlow: input.authFlow,
        AuthParameters: input.authParameters,
      }),
    respondToAuthChallenge: (input) =>
      call<AuthResult>(region, "RespondToAuthChallenge", {
        ClientId: input.clientId,
        ChallengeName: input.challengeName,
        Session: input.session,
        ChallengeResponses: input.challengeResponses,
      }),
  };
}
